<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <div class="month-selector flex justify-content-between align-items-center mb-4">
          <Button icon="pi pi-chevron-left" @click="prevMonth" class="p-button-text" />
          <h2 class="m-0">{{ formattedMonth }}</h2>
          <Button icon="pi pi-chevron-right" @click="nextMonth" class="p-button-text" />
        </div>

        <Loader v-if="loading" />

        <div v-else>
          <!-- Sezione Corsi -->
          <div class="mb-5">
            <h3><i class="pi pi-users mr-2"></i>Incassi per Corso</h3>
            <DataTable :value="data.courses" responsiveLayout="scroll" class="p-datatable-sm">
              <Column field="nome" header="Corso">
                <template #body="slotProps">
                  <span v-if="slotProps.data.id === 0">{{ slotProps.data.nome }}</span>
                  <router-link v-else :to="'/corso/' + slotProps.data.id + '/dettagli'" class="font-bold no-underline text-blue-600 hover:underline">
                    {{ slotProps.data.nome }}
                  </router-link>
                </template>
              </Column>
              <Column field="mensili" header="Mensili" headerClass="text-right" bodyClass="text-right">
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.mensili) }}
                </template>
              </Column>
              <Column field="tessere" header="Tessere" headerClass="text-right" bodyClass="text-right">
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.tessere) }}
                </template>
              </Column>
              <Column header="Totale" headerClass="text-right" bodyClass="text-right">
                <template #body="slotProps">
                  <b>{{ formatCurrency(parseFloat(slotProps.data.mensili) + parseFloat(slotProps.data.tessere)) }}</b>
                </template>
              </Column>
              <Column header="CSV" headerClass="text-center" bodyClass="text-center" style="width: 4rem">
                <template #body="slotProps">
                  <Button icon="pi pi-download" class="p-button-rounded p-button-success" @click="downloadCSV('course', slotProps.data.id, slotProps.data.nome)" v-tooltip.top="'Scarica CSV'" />
                </template>
              </Column>
            </DataTable>
          </div>

          <!-- Sezione Prodotti -->
          <div class="mb-5">
            <h3><i class="pi pi-shopping-bag mr-2"></i>Prodotti Venduti</h3>
            <DataTable :value="data.products" responsiveLayout="scroll" class="p-datatable-sm">
              <Column field="nome" header="Prodotto">
                <template #body="slotProps">
                  <router-link :to="'/prodotto/' + slotProps.data.id + '/dettagli'" class="font-bold no-underline text-blue-600 hover:underline">
                    {{ slotProps.data.nome }}
                  </router-link>
                </template>
              </Column>
              <Column field="quantita" header="Quantità" headerClass="text-right" bodyClass="text-right"></Column>
              <Column field="totale" header="Totale Incassato" headerClass="text-right" bodyClass="text-right">
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.totale) }}
                </template>
              </Column>
              <Column header="Azioni" headerClass="text-center" bodyClass="text-center" style="width: 8rem">
                <template #body="slotProps">
                  <div class="flex justify-content-center gap-2">
                    <Button icon="pi pi-download" class="p-button-rounded p-button-success" @click="downloadCSV('product', slotProps.data.nome, slotProps.data.nome)" v-tooltip.top="'Scarica CSV'" />
                    <Button v-if="slotProps.data.isAcconto == 1" icon="pi pi-cog" class="p-button-rounded p-button-text p-button-info" @click="openAccontoDialog(slotProps.data)" v-tooltip.top="'Gestisci Acconti'" />
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>

          <!-- Sezione Tornei -->
          <div class="mb-5">
            <h3><i class="pi pi-trophy mr-2"></i>Tornei</h3>
            <DataTable :value="data.tournaments" responsiveLayout="scroll" class="p-datatable-sm">
              <Column field="nome" header="Torneo">
                <template #body="slotProps">
                  <router-link to="/tornei" class="font-bold no-underline text-blue-600 hover:underline">
                    {{ slotProps.data.nome }}
                  </router-link>
                </template>
              </Column>
              <Column field="pagamenti" header="Numero Pagamenti" headerClass="text-right" bodyClass="text-right"></Column>
              <Column field="totale" header="Totale Incassato" headerClass="text-right" bodyClass="text-right">
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.totale) }}
                </template>
              </Column>
              <Column header="CSV" headerClass="text-center" bodyClass="text-center" style="width: 4rem">
                <template #body="slotProps">
                  <Button icon="pi pi-download" class="p-button-rounded p-button-success" @click="downloadCSV('tournament', slotProps.data.id, slotProps.data.nome)" v-tooltip.top="'Scarica CSV'" />
                </template>
              </Column>
            </DataTable>
          </div>

          <!-- Totale Generale -->
          <div class="flex justify-content-end mt-4 p-3 surface-100 border-round">
            <div class="text-right">
              <span class="text-xl mr-2">Totale Generale del Mese:</span>
              <span class="text-3xl font-bold text-primary">{{ formatCurrency(data.total_general) }}</span>
            </div>
          </div>
        </div>

        <!-- Dialog Gestione Acconti -->
        <Dialog v-model:visible="accontoDialogVisible" :header="'Gestione Acconti - ' + selectedProduct?.nome" :style="{width: '70vw'}" :modal="true">
          <Loader v-if="accontoLoading" />
          <div v-else>
            <DataTable :value="accontoDetails" responsiveLayout="scroll" class="p-datatable-sm">
              <Column field="data_pagamento" header="Data">
                <template #body="slotProps">
                  {{ formatDate(slotProps.data.data_pagamento) }}
                </template>
              </Column>
              <Column field="atleta" header="Atleta"></Column>
              <Column field="importo" header="Importo">
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.importo) }}
                </template>
              </Column>
              <Column header="Prodotto Acconto">
                <template #body="slotProps">
                  <Dropdown v-model="slotProps.data.prodotto_id" :options="accontoProducts" optionLabel="nome" optionValue="id" placeholder="Cambia prodotto" class="w-full" @change="saveAccontoUpdate(slotProps.data)" />
                </template>
              </Column>
              <Column header="Associa Corso">
                <template #body="slotProps">
                  <Dropdown v-model="slotProps.data.id_iscrizione" :options="slotProps.data.athleteCourses" optionLabel="nome" optionValue="id" placeholder="Nessuno" :showClear="true" class="w-full" @change="saveAccontoUpdate(slotProps.data)" />
                </template>
              </Column>
              <Column header="Associa Torneo">
                <template #body="slotProps">
                  <Dropdown v-model="slotProps.data.id_torneo_ext" :options="slotProps.data.athleteTournaments" optionLabel="nome" optionValue="id" placeholder="Nessuno" :showClear="true" class="w-full" @change="saveAccontoUpdate(slotProps.data)" />
                </template>
              </Column>
              <Column header="Associa Prodotto">
                <template #body="slotProps">
                  <Dropdown v-model="slotProps.data.id_prodotto" :options="slotProps.data.athleteProducts" optionLabel="nome" optionValue="id" placeholder="Nessuno" :showClear="true" class="w-full" @change="saveAccontoUpdate(slotProps.data)" />
                </template>
              </Column>
              <Column header="Associa Kit">
                <template #body="slotProps">
                  <Dropdown v-model="slotProps.data.id_kit" :options="slotProps.data.athleteKits" optionLabel="nome" optionValue="id" placeholder="Nessuno" :showClear="true" class="w-full" @change="saveAccontoUpdate(slotProps.data)" />
                </template>
              </Column>
              <Column header="Note">
                <template #body="slotProps">
                  <div class="p-inputgroup">
                    <InputText v-model="slotProps.data.note" placeholder="Aggiungi note..." />
                    <Button icon="pi pi-save" class="p-button-success" @click="saveAccontoUpdate(slotProps.data)" />
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </Dialog>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance, computed, watch } from 'vue';
import Loader from "@/components/Loader.vue";
import Service from '@/services/levante-api/levante-api';
import moment from 'moment';
import { useToast } from "primevue/usetoast";

export default {
  components: {
    Loader,
  },
  setup() {
    const toast = useToast();
    const loading = ref(true);
    const data = ref({
      courses: [],
      products: [],
      tournaments: [],
      total_general: 0
    });
    const currentMonth = ref(new Date());
    
    // Acconto Dialog state
    const accontoDialogVisible = ref(false);
    const accontoLoading = ref(false);
    const selectedProduct = ref(null);
    const accontoDetails = ref([]);
    const accontoProducts = ref([]);

    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    const service = new Service(self.$eventBus);

    const fetchData = async () => {
      loading.value = true;
      const monthStr = moment(currentMonth.value).format('YYYY-MM-01');
      const res = await service.getMonthlySummary(monthStr);
      // service.getMonthlySummary returns { items: res.data }
      if (res && res.items) {
          // If the data is still wrapped in an 'items' key from the controller (though we flattened it)
          // or if it's the raw object returned by getGetByUrl
          data.value = res.items;
      }
      loading.value = false;
    };

    const downloadCSV = async (type, id, name) => {
      const monthStr = moment(currentMonth.value).format('YYYY-MM-01');
      const res = await service.getMonthlySummaryDetails(type, id, monthStr);
      
      if (res && res.items) {
        const details = res.items;
        if (details.length === 0) {
          toast.add({severity:'warn', summary: 'Nessun dato', detail:'Non ci sono pagamenti per questo elemento nel mese selezionato', life: 3000});
          return;
        }

        // Generate CSV content
        const header = ["Data", "Importo", "Atleta", "Dettaglio", "Note"];
        const rows = details.map(d => [
          formatDate(d.data_pagamento),
          d.importo.toString().replace('.', ','),
          d.atleta,
          d.dettaglio || '',
          d.note || ''
        ]);

        let csvContent = header.join(";") + "\n";
        rows.forEach(row => {
          csvContent += row.join(";") + "\n";
        });

        // Trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `Incassi_${name.replace(/\s+/g, '_')}_${moment(currentMonth.value).format('MM_YYYY')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    const openAccontoDialog = async (product) => {
      selectedProduct.value = product;
      accontoDialogVisible.value = true;
      accontoLoading.value = true;
      
      const monthStr = moment(currentMonth.value).format('YYYY-MM-01');
      const [detailsRes, products] = await Promise.all([
        service.getMonthlySummaryDetails('product', product.nome, monthStr, 1),
        service.getAccontoProducts()
      ]);
      
      if (detailsRes && detailsRes.items) {
        const details = detailsRes.items;
        
        // Fetch associations for each athlete in parallel
        await Promise.all(details.map(async (item) => {
          if (item.id_atleta) {
            const associations = await service.getAthleteAssociations(item.id_atleta);
            item.athleteCourses = associations.items.courses || [];
            item.athleteTournaments = associations.items.tournaments || [];
            item.athleteProducts = associations.items.products || [];
            item.athleteKits = associations.items.kits || [];
          } else {
            item.athleteCourses = [];
            item.athleteTournaments = [];
            item.athleteProducts = [];
            item.athleteKits = [];
          }
        }));
        
        accontoDetails.value = details;
      }
      accontoProducts.value = products;
      accontoLoading.value = false;
    };


    const saveAccontoUpdate = async (item) => {
      const res = await service.updateAccontoPayment(item.id, item.prodotto_id, item.note, item.id_iscrizione, item.id_torneo_ext, item.id_prodotto, item.id_kit);
      if (res && res.item && res.item.status === 'success') {
        toast.add({severity:'success', summary: 'Aggiornato', detail: 'Pagamento aggiornato con successo', life: 3000});
        // Refresh data to reflect potential product changes in the main table
        fetchData();
      }
    };

    const formattedMonth = computed(() => {
      return moment(currentMonth.value).locale('it').format('MMMM YYYY').toUpperCase();
    });

    const prevMonth = () => {
      currentMonth.value = moment(currentMonth.value).subtract(1, 'months').toDate();
    };

    const nextMonth = () => {
      currentMonth.value = moment(currentMonth.value).add(1, 'months').toDate();
    };

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR'
      }).format(value);
    };

    const formatDate = (value) => {
      if (!value) return '';
      return moment(value).format('DD/MM/YYYY');
    };

    watch(currentMonth, () => {
      fetchData();
    });

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      data,
      formattedMonth,
      prevMonth,
      nextMonth,
      formatCurrency,
      formatDate,
      downloadCSV,
      accontoDialogVisible,
      accontoLoading,
      selectedProduct,
      accontoDetails,
      accontoProducts,
      openAccontoDialog,
      saveAccontoUpdate
    };
  }
};
</script>

<style scoped>
.month-selector {
  background: var(--surface-card);
  padding: 1rem;
  border-radius: var(--border-radius);
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}
h3 {
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 0.5rem;
  color: var(--primary-color);
}
</style>
